import { useState, type FormEvent } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  apiDelete,
  apiFetch,
  apiPost,
  friendlyError,
  type ApiError,
} from "../../api/client.js";
import { useAsync } from "../../lib/useAsync.js";

interface Assignment {
  customer_id: number;
  username: string;
  vmid: number;
  node: string;
  display_name: string | null;
  type: "qemu" | "lxc";
  assigned_at: string;
}
interface CustomerOption {
  id: number;
  username: string;
}
interface UnassignedVm {
  vmid: number;
  name: string;
  node: string;
  type: "qemu" | "lxc";
  status: string;
}
interface UnassignedVmsResponse {
  vms: UnassignedVm[];
}

export function Assignments() {
  const { data, loading, error, refetch } = useAsync<Assignment[]>(
    () => apiFetch<Assignment[]>("/api/admin/assignments"),
    []
  );
  const customers = useAsync<CustomerOption[]>(
    () => apiFetch<CustomerOption[]>("/api/admin/customers"),
    []
  );
  const unassigned = useAsync<UnassignedVm[]>(
    () => apiFetch<UnassignedVmsResponse>("/api/admin/unassigned-vms").then((r) => r.vms),
    []
  );

  const [customerId, setCustomerId] = useState<number | string>("");
  const [vmid, setVmid] = useState<string>("");
  const [type, setType] = useState("qemu");
  const [node, setNode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  function handleVmidChange(v: string | null) {
    const id = v ?? "";
    setVmid(id);
    setErrors(({ vmid: _cleared, ...rest }) => rest);
    const guest = (unassigned.data ?? []).find((g) => String(g.vmid) === id);
    if (guest) {
      setNode(guest.node);
      setType(guest.type);
    } else {
      setNode("");
      setType("qemu");
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!Number.isInteger(Number(customerId)) || Number(customerId) <= 0) {
      errs.customer = "Select a customer";
    }
    if (!Number.isInteger(Number(vmid)) || Number(vmid) <= 0) errs.vmid = "Select a guest";
    if (!node.trim()) errs.node = "Required";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      await apiPost("/api/admin/assignments", {
        customer_id: Number(customerId),
        vmid: Number(vmid),
        node: node.trim(),
        type,
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
      });
      notifications.show({
        color: "green",
        message: type === "lxc" ? "Container assigned" : "VM assigned",
      });
      setVmid("");
      setDisplayName("");
      refetch();
      unassigned.refetch();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409 && apiErr.error === "vmid_already_assigned") {
        const detail = apiErr.details as { customer_username?: string } | undefined;
        notifications.show({
          color: "red",
          message: detail?.customer_username
            ? `vmid ${vmid} already assigned to ${detail.customer_username}`
            : `vmid ${vmid} already assigned`,
        });
      } else {
        notifications.show({ color: "red", message: friendlyError(apiErr) });
      }
    } finally {
      setSubmitting(false);
    }
  }

  function confirmUnassign(a: Assignment) {
    modals.openConfirmModal({
      title: a.type === "lxc" ? "Unassign Container" : "Unassign VM",
      centered: true,
      children: (
        <Text size="sm">
          Unassign vmid <b>{a.vmid}</b> from <b>{a.username}</b>?
        </Text>
      ),
      labels: { confirm: "Unassign", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await apiDelete("/api/admin/assignments", {
            customer_id: a.customer_id,
            vmid: a.vmid,
          });
          notifications.show({
            color: "green",
            message: a.type === "lxc" ? "Container unassigned" : "VM unassigned",
          });
          refetch();
        } catch (err) {
          notifications.show({ color: "red", message: friendlyError(err) });
        }
      },
    });
  }

  return (
    <Box>
      <Title order={2} mb="md">
        VM Assignments
      </Title>

      <form onSubmit={submit}>
        <Group align="flex-start" grow wrap="wrap" mb="md">
          <Stack gap="xs" style={{ minWidth: 160 }}>
            <Text size="sm" fw={500}>
              Customer
            </Text>
            <CustomerSelect
              value={customerId}
              onChange={(v) => {
                setCustomerId(v ? Number(v) : "");
                setErrors(({ customer: _cleared, ...rest }) => rest);
              }}
              options={customers.data ?? []}
              error={errors.customer}
            />
          </Stack>
          <Stack gap="xs" style={{ minWidth: 120 }}>
            <Text size="sm" fw={500}>
              Type
            </Text>
            <Select
              value={type}
              onChange={(v) => setType(v ?? "qemu")}
              data={[
                { value: "qemu", label: "VM (qemu)" },
                { value: "lxc", label: "Container (LXC)" },
              ]}
            />
          </Stack>
          <Stack gap="xs" style={{ minWidth: 220 }}>
            <Text size="sm" fw={500}>
              VM
            </Text>
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Select
                value={vmid}
                onChange={handleVmidChange}
                data={(unassigned.data ?? []).map((g) => ({
                  value: String(g.vmid),
                  label: `${g.vmid} — ${g.name} (${g.type === "lxc" ? "CT" : "VM"})`,
                }))}
                searchable
                nothingFoundMessage="All guests assigned"
                placeholder="Select…"
                error={unassigned.error ?? errors.vmid}
                style={{ flex: 1 }}
              />
              <ActionIcon
                variant="default"
                loading={unassigned.loading}
                onClick={() => unassigned.refetch()}
                title="Refresh unassigned guests"
                aria-label="Refresh unassigned guests"
              >
                <IconRefresh size={16} stroke={1.75} />
              </ActionIcon>
            </Group>
          </Stack>
          <Stack gap="xs" style={{ minWidth: 140 }}>
            <Text size="sm" fw={500}>
              Node
            </Text>
            <TextInput
              value={node}
              onChange={(e) => {
                setNode(e.target.value);
                setErrors(({ node: _cleared, ...rest }) => rest);
              }}
              error={errors.node}
              placeholder="pve1"
            />
          </Stack>
          <Stack gap="xs" style={{ minWidth: 180 }}>
            <Text size="sm" fw={500}>
              Display name (optional)
            </Text>
            <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="web-01" />
          </Stack>
          <Stack gap="xs" justify="flex-end" style={{ minWidth: 120 }}>
            <Button type="submit" loading={submitting}>
              Assign
            </Button>
          </Stack>
        </Group>
      </form>

      {error && (
        <Text c="red" mb="sm">
          {error}
        </Text>
      )}
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Customer</Table.Th>
            <Table.Th>VMID</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Node</Table.Th>
            <Table.Th>Display name</Table.Th>
            <Table.Th>Assigned</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text c="dimmed">Loading…</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && data && data.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text c="dimmed">No VM assignments.</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {data?.map((a) => (
            <Table.Tr key={`${a.customer_id}-${a.vmid}`}>
              <Table.Td>{a.username}</Table.Td>
              <Table.Td>{a.vmid}</Table.Td>
              <Table.Td>{a.type === "lxc" ? "Container" : "VM"}</Table.Td>
              <Table.Td>{a.node}</Table.Td>
              <Table.Td>{a.display_name ?? "—"}</Table.Td>
              <Table.Td>{a.assigned_at}</Table.Td>
              <Table.Td>
                <ActionIcon variant="default" color="red" size="sm" onClick={() => confirmUnassign(a)} title="Unassign">
                  <Text size="xs">×</Text>
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

interface CustomerSelectProps {
  value: number | string;
  onChange: (v: string | null) => void;
  options: CustomerOption[];
  error?: string;
}

function CustomerSelect({ value, onChange, options, error }: CustomerSelectProps) {
  const data = options.map((c) => ({ value: String(c.id), label: c.username }));
  return (
    <Select
      value={value === "" ? "" : String(value)}
      onChange={onChange}
      data={data}
      placeholder="Select…"
      error={error}
    />
  );
}
